/*
 * Copyright 2023 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ConnectError } from '@connectrpc/connect';
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { timestampDate } from '@bufbuild/protobuf/wkt';
import { ErrorInfoSchema } from '@buf/googleapis_googleapis.bufbuild_es/google/rpc/error_details_pb';
import { Rule } from '@yorkie-js/schema';
import { Code, YorkieError } from '@yorkie-js/sdk/src/util/error';
import { Indexable } from '@yorkie-js/sdk/src/document/document';
import {
  PresenceChange,
  PresenceChangeType,
} from '@yorkie-js/sdk/src/document/presence/change';
import {
  InitialTimeTicket,
  TimeTicket,
} from '@yorkie-js/sdk/src/document/time/ticket';
import { ActorID } from '@yorkie-js/sdk/src/document/time/actor_id';
import { Operation } from '@yorkie-js/sdk/src/document/operation/operation';
import { SetOperation } from '@yorkie-js/sdk/src/document/operation/set_operation';
import { AddOperation } from '@yorkie-js/sdk/src/document/operation/add_operation';
import { MoveOperation } from '@yorkie-js/sdk/src/document/operation/move_operation';
import { RemoveOperation } from '@yorkie-js/sdk/src/document/operation/remove_operation';
import { EditOperation } from '@yorkie-js/sdk/src/document/operation/edit_operation';
import { StyleOperation } from '@yorkie-js/sdk/src/document/operation/style_operation';
import { TreeEditOperation } from '@yorkie-js/sdk/src/document/operation/tree_edit_operation';
import { ChangeID } from '@yorkie-js/sdk/src/document/change/change_id';
import { Change } from '@yorkie-js/sdk/src/document/change/change';
import { ChangePack } from '@yorkie-js/sdk/src/document/change/change_pack';
import { Checkpoint } from '@yorkie-js/sdk/src/document/change/checkpoint';
import { ElementRHT } from '@yorkie-js/sdk/src/document/crdt/element_rht';
import { RGATreeList } from '@yorkie-js/sdk/src/document/crdt/rga_tree_list';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import { CRDTObject } from '@yorkie-js/sdk/src/document/crdt/object';
import { CRDTArray } from '@yorkie-js/sdk/src/document/crdt/array';
import { VersionVector } from '@yorkie-js/sdk/src/document/time/version_vector';
import { CRDTTreePos } from './../document/crdt/tree';
import {
  RGATreeSplit,
  RGATreeSplitNode,
  RGATreeSplitNodeID,
  RGATreeSplitPos,
  RestoreSpan,
} from '@yorkie-js/sdk/src/document/crdt/rga_tree_split';
import { CRDTText, CRDTTextValue } from '@yorkie-js/sdk/src/document/crdt/text';
import {
  Primitive,
  PrimitiveType,
} from '@yorkie-js/sdk/src/document/crdt/primitive';
import {
  Change as PbChange,
  ChangeSchema as PbChangeSchema,
  ChangeID as PbChangeID,
  ChangeIDSchema as PbChangeIDSchema,
  ChangePack as PbChangePack,
  ChangePackSchema as PbChangePackSchema,
  Checkpoint as PbCheckpoint,
  CheckpointSchema as PbCheckpointSchema,
  Rule as PbRule,
  JSONElement as PbJSONElement,
  JSONElementSchema as PbJSONElementSchema,
  JSONElementSimple as PbJSONElementSimple,
  JSONElementSimpleSchema as PbJSONElementSimpleSchema,
  NodeAttr as PbNodeAttr,
  NodeAttrSchema as PbNodeAttrSchema,
  Operation as PbOperation,
  OperationSchema as PbOperationSchema,
  Presence as PbPresence,
  PresenceSchema as PbPresenceSchema,
  PresenceChange as PbPresenceChange,
  PresenceChangeSchema as PbPresenceChangeSchema,
  RGANode as PbRGANode,
  RGANodeSchema as PbRGANodeSchema,
  RHTNode as PbRHTNode,
  RHTNodeSchema as PbRHTNodeSchema,
  SnapshotSchema as PbSnapshotSchema,
  TextNode as PbTextNode,
  TextNodeSchema as PbTextNodeSchema,
  TextNodeID as PbTextNodeID,
  TextNodeIDSchema as PbTextNodeIDSchema,
  TextNodePos as PbTextNodePos,
  TextNodePosSchema as PbTextNodePosSchema,
  TimeTicket as PbTimeTicket,
  TimeTicketSchema as PbTimeTicketSchema,
  TreeNode as PbTreeNode,
  TreeNodeSchema as PbTreeNodeSchema,
  TreeNodes as PbTreeNodes,
  TreeNodesSchema as PbTreeNodesSchema,
  TreePos as PbTreePos,
  TreePosSchema as PbTreePosSchema,
  TreeNodeID as PbTreeNodeID,
  TreeNodeIDSchema as PbTreeNodeIDSchema,
  VersionVector as PbVersionVector,
  VersionVectorSchema as PbVersionVectorSchema,
  ValueType as PbValueType,
  JSONElement_Tree as PbJSONElement_Tree,
  JSONElement_TreeSchema as PbJSONElement_TreeSchema,
  JSONElement_Text as PbJSONElement_Text,
  JSONElement_TextSchema as PbJSONElement_TextSchema,
  JSONElement_Primitive as PbJSONElement_Primitive,
  JSONElement_PrimitiveSchema as PbJSONElement_PrimitiveSchema,
  JSONElement_Counter as PbJSONElement_Counter,
  JSONElement_CounterSchema as PbJSONElement_CounterSchema,
  JSONElement_JSONObject as PbJSONElement_JSONObject,
  JSONElement_JSONObjectSchema as PbJSONElement_JSONObjectSchema,
  JSONElement_JSONArray as PbJSONElement_JSONArray,
  JSONElement_JSONArraySchema as PbJSONElement_JSONArraySchema,
  PresenceChange_ChangeType as PbPresenceChange_ChangeType,
  Operation_SetSchema as PbOperation_SetSchema,
  Operation_AddSchema as PbOperation_AddSchema,
  Operation_MoveSchema as PbOperation_MoveSchema,
  Operation_RemoveSchema as PbOperation_RemoveSchema,
  Operation_EditSchema as PbOperation_EditSchema,
  Operation_StyleSchema as PbOperation_StyleSchema,
  Operation_IncreaseSchema as PbOperation_IncreaseSchema,
  Operation_TreeEditSchema as PbOperation_TreeEditSchema,
  Operation_TreeStyleSchema as PbOperation_TreeStyleSchema,
  Operation_ArraySetSchema as PbOperation_ArraySetSchema,
  RestoreSpanSchema as PbRestoreSpanSchema,
  RestoreSpan as PbRestoreSpan,
  RestoreMode as PbRestoreMode,
  TreeRestoreSpanSchema as PbTreeRestoreSpanSchema,
  TreeRestoreSpan as PbTreeRestoreSpan,
  RevisionSummary as PbRevisionSummary,
} from '@yorkie-js/sdk/src/api/yorkie/v1/resources_pb';
import { IncreaseOperation } from '@yorkie-js/sdk/src/document/operation/increase_operation';
import {
  CounterType,
  CRDTCounter,
} from '@yorkie-js/sdk/src/document/crdt/counter';
import {
  CRDTTree,
  CRDTTreeNode,
  CRDTTreeNodeID,
  TreeRestoreSpan,
} from '@yorkie-js/sdk/src/document/crdt/tree';
import { traverseAll } from '../util/index_tree';
import { TreeStyleOperation } from '../document/operation/tree_style_operation';
import { RHT } from '../document/crdt/rht';
import { ArraySetOperation } from '../document/operation/array_set_operation';
import { RevisionSummary } from './revision';

/**
 * `toPresence` converts the given model to Protobuf format.
 */
function toPresence(presence: Indexable): PbPresence {
  const pbDataMap: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(presence)) {
    pbDataMap[key] = JSON.stringify(value);
  }
  return create(PbPresenceSchema, { data: pbDataMap });
}

/**
 * `toPresenceChange` converts the given model to Protobuf format.
 */
function toPresenceChange(
  presenceChange: PresenceChange<Indexable>,
): PbPresenceChange {
  if (presenceChange.type === PresenceChangeType.Put) {
    return create(PbPresenceChangeSchema, {
      type: PbPresenceChange_ChangeType.PUT,
      presence: toPresence(presenceChange.presence),
    });
  }
  if (presenceChange.type === PresenceChangeType.Clear) {
    return create(PbPresenceChangeSchema, {
      type: PbPresenceChange_ChangeType.CLEAR,
    });
  }

  throw new YorkieError(Code.ErrUnimplemented, `unimplemented type`);
}

/**
 * `toCheckpoint` converts the given model to Protobuf format.
 */
function toCheckpoint(checkpoint: Checkpoint): PbCheckpoint {
  return create(PbCheckpointSchema, {
    serverSeq: checkpoint.getServerSeq(),
    clientSeq: checkpoint.getClientSeq(),
  });
}

/**
 * `toChangeID` converts the given model to Protobuf format.
 */
function toChangeID(changeID: ChangeID): PbChangeID {
  return create(PbChangeIDSchema, {
    clientSeq: changeID.getClientSeq(),
    lamport: changeID.getLamport(),
    actorId: toUint8Array(changeID.getActorID()),
    versionVector: toVersionVector(changeID.getVersionVector()),
  });
}

/**
 * `toTimeTicket` converts the given model to Protobuf format.
 */
function toTimeTicket(ticket?: TimeTicket): PbTimeTicket | undefined {
  if (!ticket) {
    return;
  }

  return create(PbTimeTicketSchema, {
    lamport: ticket.getLamport(),
    delimiter: ticket.getDelimiter(),
    actorId: toUint8Array(ticket.getActorID()),
  });
}

/**
 * `toVersionVector` converts the given model to Protobuf format.
 */
function toVersionVector(vector?: VersionVector): PbVersionVector | undefined {
  if (!vector) {
    return;
  }

  const pbVector = create(PbVersionVectorSchema);
  for (const [actorID, lamport] of vector) {
    const base64ActorID = uint8ArrayToBase64(toUint8Array(actorID));
    pbVector.vector[base64ActorID] = BigInt(lamport.toString());
  }
  return pbVector;
}

/**
 * `toValueType` converts the given model to Protobuf format.
 */
function toValueType(valueType: PrimitiveType): PbValueType {
  switch (valueType) {
    case PrimitiveType.Null:
      return PbValueType.NULL;
    case PrimitiveType.Boolean:
      return PbValueType.BOOLEAN;
    case PrimitiveType.Integer:
      return PbValueType.INTEGER;
    case PrimitiveType.Long:
      return PbValueType.LONG;
    case PrimitiveType.Double:
      return PbValueType.DOUBLE;
    case PrimitiveType.String:
      return PbValueType.STRING;
    case PrimitiveType.Bytes:
      return PbValueType.BYTES;
    case PrimitiveType.Date:
      return PbValueType.DATE;
    default:
      throw new YorkieError(
        Code.ErrInvalidType,
        `unsupported type: ${valueType}`,
      );
  }
}

/**
 * `toCounterType` converts the given model to Protobuf format.
 */
function toCounterType(valueType: CounterType): PbValueType {
  switch (valueType) {
    case CounterType.Int:
      return PbValueType.INTEGER_CNT;
    case CounterType.Long:
      return PbValueType.LONG_CNT;
    case CounterType.IntDedup:
      return PbValueType.INTEGER_DEDUP_CNT;
    default:
      throw new YorkieError(
        Code.ErrInvalidType,
        `unsupported type: ${valueType}`,
      );
  }
}

/**
 * `toElementSimple` converts the given model to Protobuf format.
 */
function toElementSimple(element: CRDTElement): PbJSONElementSimple {
  if (element instanceof CRDTObject) {
    return create(PbJSONElementSimpleSchema, {
      type: PbValueType.JSON_OBJECT,
      createdAt: toTimeTicket(element.getCreatedAt()),
      value: objectToBytes(element),
    });
  }
  if (element instanceof CRDTArray) {
    return create(PbJSONElementSimpleSchema, {
      type: PbValueType.JSON_ARRAY,
      createdAt: toTimeTicket(element.getCreatedAt()),
      value: arrayToBytes(element),
    });
  }
  if (element instanceof CRDTText) {
    return create(PbJSONElementSimpleSchema, {
      type: PbValueType.TEXT,
      createdAt: toTimeTicket(element.getCreatedAt()),
    });
  }
  if (element instanceof Primitive) {
    return create(PbJSONElementSimpleSchema, {
      type: toValueType(element.getType()),
      createdAt: toTimeTicket(element.getCreatedAt()),
      value: element.toBytes(),
    });
  }
  if (element instanceof CRDTCounter) {
    return create(PbJSONElementSimpleSchema, {
      type: toCounterType(element.getType()),
      createdAt: toTimeTicket(element.getCreatedAt()),
      value: element.toBytes(),
    });
  }
  if (element instanceof CRDTTree) {
    return create(PbJSONElementSimpleSchema, {
      type: PbValueType.TREE,
      createdAt: toTimeTicket(element.getCreatedAt()),
      value: treeToBytes(element),
    });
  }

  throw new YorkieError(Code.ErrUnimplemented, `unimplemented element`);
}

/**
 * `toTextNodeID` converts the given model to Protobuf format.
 */
function toTextNodeID(id: RGATreeSplitNodeID): PbTextNodeID {
  return create(PbTextNodeIDSchema, {
    createdAt: toTimeTicket(id.getCreatedAt()),
    offset: id.getOffset(),
  });
}

/**
 * `toTextNodePos` converts the given model to Protobuf format.
 */
function toTextNodePos(pos: RGATreeSplitPos): PbTextNodePos {
  return create(PbTextNodePosSchema, {
    createdAt: toTimeTicket(pos.getID().getCreatedAt()),
    offset: pos.getID().getOffset(),
    relativeOffset: pos.getRelativeOffset(),
  });
}

/**
 * `toTreePos` converts the given model to Protobuf format.
 */
function toTreePos(pos: CRDTTreePos): PbTreePos {
  return create(PbTreePosSchema, {
    parentId: toTreeNodeID(pos.getParentID()),
    leftSiblingId: toTreeNodeID(pos.getLeftSiblingID()),
  });
}

/**
 * `toTreeNodeID` converts the given model to Protobuf format.
 */
function toTreeNodeID(treeNodeID: CRDTTreeNodeID): PbTreeNodeID {
  return create(PbTreeNodeIDSchema, {
    createdAt: toTimeTicket(treeNodeID.getCreatedAt()),
    offset: treeNodeID.getOffset(),
  });
}

/**
 * `toPbTreeRestoreSpan` converts a TreeRestoreSpan to Protobuf format.
 */
function toPbTreeRestoreSpan(span: TreeRestoreSpan): PbTreeRestoreSpan {
  const pbSpan = create(PbTreeRestoreSpanSchema);
  pbSpan.id = toTreeNodeID(span.id);
  pbSpan.nodeType = span.nodeType;
  pbSpan.isText = span.isText;
  pbSpan.length = span.length;
  if (span.value !== undefined) {
    pbSpan.value = span.value;
  }
  if (span.attrs) {
    pbSpan.attributes = toRHT(span.attrs);
  }
  if (span.parentID) {
    pbSpan.parentId = toTreeNodeID(span.parentID);
  }
  if (span.leftSiblingID) {
    pbSpan.leftSiblingId = toTreeNodeID(span.leftSiblingID);
  }
  if (span.rightSiblingID) {
    pbSpan.rightSiblingId = toTreeNodeID(span.rightSiblingID);
  }
  return pbSpan;
}

/**
 * `fromPbTreeRestoreSpan` converts a Protobuf TreeRestoreSpan to model format.
 * A span addresses content by insertion identity, so every node ID it carries
 * is malformed without a `createdAt`, and the attribute snapshot is malformed
 * without an `updatedAt`. Both are rejected here because the decoders below
 * non-null assert those tickets: `fromTreeNodeID` would yield a
 * CRDTTreeNodeID with an undefined createdAt and `fromRHT` an RHT node with an
 * undefined updatedAt, neither failing until deep inside the restore path.
 * Mirrors the server's `fromTreeRestoreSpans`.
 */
function fromPbTreeRestoreSpan(pbSpan: PbTreeRestoreSpan): TreeRestoreSpan {
  const anchors = [
    pbSpan.parentId,
    pbSpan.leftSiblingId,
    pbSpan.rightSiblingId,
  ];
  if (
    !pbSpan.id?.createdAt ||
    anchors.some((anchor) => anchor && !anchor.createdAt) ||
    Object.values(pbSpan.attributes).some((attr) => !attr.updatedAt)
  ) {
    throw new YorkieError(
      Code.ErrInvalidArgument,
      'malformed tree restore span: missing timestamp',
    );
  }
  return {
    id: fromTreeNodeID(pbSpan.id),
    nodeType: pbSpan.nodeType,
    isText: pbSpan.isText,
    length: pbSpan.length,
    value: pbSpan.isText ? pbSpan.value : undefined,
    attrs:
      Object.keys(pbSpan.attributes).length > 0
        ? fromRHT(pbSpan.attributes)
        : undefined,
    parentID: pbSpan.parentId ? fromTreeNodeID(pbSpan.parentId) : undefined,
    leftSiblingID: pbSpan.leftSiblingId
      ? fromTreeNodeID(pbSpan.leftSiblingId)
      : undefined,
    rightSiblingID: pbSpan.rightSiblingId
      ? fromTreeNodeID(pbSpan.rightSiblingId)
      : undefined,
  };
}

/**
 * `toOperation` converts the given model to Protobuf format.
 */
function toOperation(operation: Operation): PbOperation {
  const pbOperation = create(PbOperationSchema);

  if (operation instanceof SetOperation) {
    const setOperation = operation as SetOperation;
    const pbSetOperation = create(PbOperation_SetSchema);
    pbSetOperation.parentCreatedAt = toTimeTicket(
      setOperation.getParentCreatedAt(),
    );
    pbSetOperation.key = setOperation.getKey();
    pbSetOperation.value = toElementSimple(setOperation.getValue());
    pbSetOperation.executedAt = toTimeTicket(setOperation.getExecutedAt());
    pbOperation.body.case = 'set';
    pbOperation.body.value = pbSetOperation;
  } else if (operation instanceof AddOperation) {
    const addOperation = operation as AddOperation;
    const pbAddOperation = create(PbOperation_AddSchema);
    pbAddOperation.parentCreatedAt = toTimeTicket(
      addOperation.getParentCreatedAt(),
    );
    pbAddOperation.prevCreatedAt = toTimeTicket(
      addOperation.getPrevCreatedAt(),
    );
    pbAddOperation.value = toElementSimple(addOperation.getValue());
    pbAddOperation.executedAt = toTimeTicket(addOperation.getExecutedAt());
    pbOperation.body.case = 'add';
    pbOperation.body.value = pbAddOperation;
  } else if (operation instanceof MoveOperation) {
    const moveOperation = operation as MoveOperation;
    const pbMoveOperation = create(PbOperation_MoveSchema);
    pbMoveOperation.parentCreatedAt = toTimeTicket(
      moveOperation.getParentCreatedAt(),
    );
    pbMoveOperation.prevCreatedAt = toTimeTicket(
      moveOperation.getPrevCreatedAt(),
    );
    pbMoveOperation.createdAt = toTimeTicket(moveOperation.getCreatedAt());
    pbMoveOperation.executedAt = toTimeTicket(moveOperation.getExecutedAt());
    pbOperation.body.case = 'move';
    pbOperation.body.value = pbMoveOperation;
  } else if (operation instanceof RemoveOperation) {
    const removeOperation = operation as RemoveOperation;
    const pbRemoveOperation = create(PbOperation_RemoveSchema);
    pbRemoveOperation.parentCreatedAt = toTimeTicket(
      removeOperation.getParentCreatedAt(),
    );
    pbRemoveOperation.createdAt = toTimeTicket(removeOperation.getCreatedAt());
    pbRemoveOperation.executedAt = toTimeTicket(
      removeOperation.getExecutedAt(),
    );
    pbOperation.body.case = 'remove';
    pbOperation.body.value = pbRemoveOperation;
  } else if (operation instanceof EditOperation) {
    const editOperation = operation as EditOperation;
    const pbEditOperation = create(PbOperation_EditSchema);
    pbEditOperation.parentCreatedAt = toTimeTicket(
      editOperation.getParentCreatedAt(),
    );
    pbEditOperation.from = toTextNodePos(editOperation.getFromPos());
    pbEditOperation.to = toTextNodePos(editOperation.getToPos());
    pbEditOperation.content = editOperation.getContent();
    const pbAttributes = pbEditOperation.attributes;
    for (const [key, value] of editOperation.getAttributes()) {
      pbAttributes[key] = value;
    }
    pbEditOperation.executedAt = toTimeTicket(editOperation.getExecutedAt());
    const toPbSpan = (span: RestoreSpan<CRDTTextValue>) => {
      const pbSpan = create(PbRestoreSpanSchema);
      pbSpan.createdAt = toTimeTicket(span.createdAt);
      pbSpan.start = span.start;
      pbSpan.end = span.end;
      pbSpan.content = span.value.getContent();
      const pbSpanAttrs = pbSpan.attributes;
      for (const [key, value] of Object.entries(span.value.getAttributes())) {
        pbSpanAttrs[key] = value;
      }
      return pbSpan;
    };
    const restoreSpans = editOperation.getRestoreSpans();
    const retombstoneSpans = editOperation.getRetombstoneSpans();
    if (
      (restoreSpans && restoreSpans.length) ||
      (retombstoneSpans && retombstoneSpans.length)
    ) {
      pbEditOperation.restoreSpans = (restoreSpans ?? []).map(toPbSpan);
      pbEditOperation.retombstoneSpans = (retombstoneSpans ?? []).map(toPbSpan);
      pbEditOperation.restoreMode =
        editOperation.getRestoreMode() === 'retombstone'
          ? PbRestoreMode.RETOMBSTONE
          : PbRestoreMode.RESTORE;
    }
    pbOperation.body.case = 'edit';
    pbOperation.body.value = pbEditOperation;
  } else if (operation instanceof StyleOperation) {
    const styleOperation = operation as StyleOperation;
    const pbStyleOperation = create(PbOperation_StyleSchema);
    pbStyleOperation.parentCreatedAt = toTimeTicket(
      styleOperation.getParentCreatedAt(),
    );
    pbStyleOperation.from = toTextNodePos(styleOperation.getFromPos());
    pbStyleOperation.to = toTextNodePos(styleOperation.getToPos());

    const attributesToRemove = styleOperation.getAttributesToRemove();
    if (attributesToRemove.length > 0) {
      pbStyleOperation.attributesToRemove = attributesToRemove;
    }
    const pbAttributes = pbStyleOperation.attributes;
    for (const [key, value] of styleOperation.getAttributes()) {
      pbAttributes[key] = value;
    }
    pbStyleOperation.executedAt = toTimeTicket(styleOperation.getExecutedAt());
    pbOperation.body.case = 'style';
    pbOperation.body.value = pbStyleOperation;
  } else if (operation instanceof IncreaseOperation) {
    const increaseOperation = operation as IncreaseOperation;
    const pbIncreaseOperation = create(PbOperation_IncreaseSchema);
    pbIncreaseOperation.parentCreatedAt = toTimeTicket(
      increaseOperation.getParentCreatedAt(),
    );
    pbIncreaseOperation.value = toElementSimple(increaseOperation.getValue());
    pbIncreaseOperation.executedAt = toTimeTicket(
      increaseOperation.getExecutedAt(),
    );
    pbIncreaseOperation.actor = increaseOperation.getActor();
    pbOperation.body.case = 'increase';
    pbOperation.body.value = pbIncreaseOperation;
  } else if (operation instanceof TreeEditOperation) {
    const treeEditOperation = operation as TreeEditOperation;
    const pbTreeEditOperation = create(PbOperation_TreeEditSchema);
    pbTreeEditOperation.parentCreatedAt = toTimeTicket(
      treeEditOperation.getParentCreatedAt(),
    );
    pbTreeEditOperation.from = toTreePos(treeEditOperation.getFromPos());
    pbTreeEditOperation.to = toTreePos(treeEditOperation.getToPos());
    pbTreeEditOperation.contents = toTreeNodesWhenEdit(
      treeEditOperation.getContents()!,
    );
    pbTreeEditOperation.splitLevel = treeEditOperation.getSplitLevel();

    pbTreeEditOperation.executedAt = toTimeTicket(
      treeEditOperation.getExecutedAt(),
    );
    const treeRestoreSpans = treeEditOperation.getRestoreSpans();
    const treeRetombstoneSpans = treeEditOperation.getRetombstoneSpans();
    if (
      (treeRestoreSpans && treeRestoreSpans.length) ||
      (treeRetombstoneSpans && treeRetombstoneSpans.length)
    ) {
      pbTreeEditOperation.restoreSpans = (treeRestoreSpans ?? []).map(
        toPbTreeRestoreSpan,
      );
      pbTreeEditOperation.retombstoneSpans = (treeRetombstoneSpans ?? []).map(
        toPbTreeRestoreSpan,
      );
      pbTreeEditOperation.restoreMode =
        treeEditOperation.getRestoreMode() === 'retombstone'
          ? PbRestoreMode.RETOMBSTONE
          : PbRestoreMode.RESTORE;
    }
    pbTreeEditOperation.splitTickets = treeEditOperation
      .getSplitTickets()
      .map((ticket) => toTimeTicket(ticket)!);
    pbOperation.body.case = 'treeEdit';
    pbOperation.body.value = pbTreeEditOperation;
  } else if (operation instanceof TreeStyleOperation) {
    const treeStyleOperation = operation as TreeStyleOperation;
    const pbTreeStyleOperation = create(PbOperation_TreeStyleSchema);
    pbTreeStyleOperation.parentCreatedAt = toTimeTicket(
      treeStyleOperation.getParentCreatedAt(),
    );
    pbTreeStyleOperation.from = toTreePos(treeStyleOperation.getFromPos());
    pbTreeStyleOperation.to = toTreePos(treeStyleOperation.getToPos());

    const attributesToRemove = treeStyleOperation.getAttributesToRemove();
    if (attributesToRemove.length > 0) {
      pbTreeStyleOperation.attributesToRemove = attributesToRemove;
    } else {
      const attributesMap = pbTreeStyleOperation.attributes;

      for (const [key, value] of treeStyleOperation.getAttributes()) {
        attributesMap[key] = value;
      }
    }
    pbTreeStyleOperation.executedAt = toTimeTicket(
      treeStyleOperation.getExecutedAt(),
    );
    pbOperation.body.case = 'treeStyle';
    pbOperation.body.value = pbTreeStyleOperation;
  } else if (operation instanceof ArraySetOperation) {
    const arraySetOperation = operation as ArraySetOperation;
    const pbArraySetOperation = create(PbOperation_ArraySetSchema);
    pbArraySetOperation.parentCreatedAt = toTimeTicket(
      arraySetOperation.getParentCreatedAt(),
    );
    pbArraySetOperation.createdAt = toTimeTicket(
      arraySetOperation.getCreatedAt(),
    );
    pbArraySetOperation.value = toElementSimple(arraySetOperation.getValue());
    pbArraySetOperation.executedAt = toTimeTicket(
      arraySetOperation.getExecutedAt(),
    );
    pbOperation.body.case = 'arraySet';
    pbOperation.body.value = pbArraySetOperation;
  } else {
    throw new YorkieError(Code.ErrUnimplemented, 'unimplemented operation');
  }

  return pbOperation;
}

/**
 * `toOperations` converts the given model to Protobuf format.
 */
function toOperations(operations: Array<Operation>): Array<PbOperation> {
  const pbOperations = [];
  for (const operation of operations) {
    pbOperations.push(toOperation(operation));
  }
  return pbOperations;
}

/**
 * `toChange` converts the given model to Protobuf format.
 */
function toChange(change: Change<Indexable>): PbChange {
  const pbChange = create(PbChangeSchema, {
    id: toChangeID(change.getID()),
    message: change.getMessage(),
  });
  if (change.hasOperations()) {
    pbChange.operations = toOperations(change.getOperations());
  }
  if (change.hasPresenceChange()) {
    pbChange.presenceChange = toPresenceChange(change.getPresenceChange()!);
  }
  return pbChange;
}

/**
 * `toChanges` converts the given model to Protobuf format.
 */
function toChanges(changes: Array<Change<Indexable>>): Array<PbChange> {
  const pbChanges = [];
  for (const change of changes) {
    pbChanges.push(toChange(change));
  }
  return pbChanges;
}

/**
 * `toRHTNodes` converts the given model to Protobuf format.
 */
function toRHTNodes(rht: ElementRHT): Array<PbRHTNode> {
  const pbRHTNodes = [];
  for (const rhtNode of rht) {
    pbRHTNodes.push(
      create(PbRHTNodeSchema, {
        key: rhtNode.getStrKey(),
        element: toElement(rhtNode.getValue()),
      }),
    );
  }

  return pbRHTNodes;
}

/**
 * `toRGANodes` converts the given model to Protobuf format.
 * Includes both live element nodes and dead position nodes.
 */
function toRGANodes(arr: CRDTArray): Array<PbRGANode> {
  const pbRGANodes = [];
  for (const rgaNode of arr.getAllRGANodes()) {
    if (!rgaNode.getElementEntry()) {
      // Dead position node (abandoned by a move).
      pbRGANodes.push(
        create(PbRGANodeSchema, {
          positionCreatedAt: toTimeTicket(rgaNode.getPositionCreatedAt()),
          positionRemovedAt: toTimeTicket(rgaNode.getRemovedAt()),
        }),
      );
      continue;
    }

    const pbNode = create(PbRGANodeSchema, {
      element: toElement(rgaNode.getValue()),
    });
    if (rgaNode.getPositionMovedAt()) {
      pbNode.positionMovedAt = toTimeTicket(rgaNode.getPositionMovedAt());
      pbNode.positionCreatedAt = toTimeTicket(rgaNode.getPositionCreatedAt());
    }
    pbRGANodes.push(pbNode);
  }

  return pbRGANodes;
}

/**
 * `toTextNodes` converts the given model to Protobuf format.
 */
function toTextNodes(
  rgaTreeSplit: RGATreeSplit<CRDTTextValue>,
): Array<PbTextNode> {
  const pbTextNodes = [];

  for (const textNode of rgaTreeSplit) {
    const pbTextNode = create(PbTextNodeSchema);
    pbTextNode.id = toTextNodeID(textNode.getID());
    pbTextNode.value = textNode.getValue().getContent();
    pbTextNode.removedAt = toTimeTicket(textNode.getRemovedAt());

    const pbNodeAttrsMap = pbTextNode.attributes;
    const attrs = textNode.getValue().getAttrs();
    for (const attr of attrs) {
      const pbNodeAttr = create(PbNodeAttrSchema);
      pbNodeAttr.value = attr.getValue();
      pbNodeAttr.updatedAt = toTimeTicket(attr.getUpdatedAt());
      pbNodeAttrsMap[attr.getKey()] = pbNodeAttr;
    }

    pbTextNodes.push(pbTextNode);
  }

  return pbTextNodes;
}

/**
 * `toTreeNodesWhenEdit` converts the given model to Protobuf format.
 */
function toTreeNodesWhenEdit(nodes: Array<CRDTTreeNode>): Array<PbTreeNodes> {
  const pbTreeNodesList: Array<PbTreeNodes> = [];
  if (!nodes || !nodes.length) {
    return pbTreeNodesList;
  }

  for (const node of nodes) {
    pbTreeNodesList.push(
      create(PbTreeNodesSchema, {
        content: toTreeNodes(node),
      }),
    );
  }

  return pbTreeNodesList;
}

/**
 * `toRHT` converts the given model to Protobuf format.
 */
function toRHT(rht: RHT): { [key: string]: PbNodeAttr } {
  const pbRHT: { [key: string]: PbNodeAttr } = {};
  for (const node of rht) {
    pbRHT[node.getKey()] = create(PbNodeAttrSchema, {
      value: node.getValue(),
      updatedAt: toTimeTicket(node.getUpdatedAt()),
      isRemoved: node.isRemoved(),
    });
  }

  return pbRHT;
}

/**
 * `toTreeNodes` converts the given model to Protobuf format.
 */
function toTreeNodes(node: CRDTTreeNode): Array<PbTreeNode> {
  if (!node) {
    return [];
  }

  const pbTreeNodes: Array<PbTreeNode> = [];
  traverseAll(node, (n, depth) => {
    const pbTreeNode = create(PbTreeNodeSchema, {
      id: toTreeNodeID(n.id),
      type: n.type,
      removedAt: toTimeTicket(n.removedAt),
      depth,
    });

    if (n.isText) {
      pbTreeNode.value = n.value;
    }
    if (n.insPrevID) {
      pbTreeNode.insPrevId = toTreeNodeID(n.insPrevID);
    }
    if (n.insNextID) {
      pbTreeNode.insNextId = toTreeNodeID(n.insNextID);
    }

    if (n.mergedFrom) {
      pbTreeNode.mergedFrom = toTreeNodeID(n.mergedFrom);
    }
    if (n.mergedAt) {
      pbTreeNode.mergedAt = toTimeTicket(n.mergedAt);
    }

    if (n.attrs) {
      pbTreeNode.attributes = toRHT(n.attrs);
    }

    pbTreeNodes.push(pbTreeNode);
  });

  return pbTreeNodes;
}

/**
 * `toObject` converts the given model to Protobuf format.
 */
function toObject(obj: CRDTObject): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'jsonObject',
      value: create(PbJSONElement_JSONObjectSchema, {
        nodes: toRHTNodes(obj.getRHT()),
        createdAt: toTimeTicket(obj.getCreatedAt()),
        movedAt: toTimeTicket(obj.getMovedAt()),
        removedAt: toTimeTicket(obj.getRemovedAt()),
      }),
    },
  });
}

/**
 * `toArray` converts the given model to Protobuf format.
 */
function toArray(arr: CRDTArray): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'jsonArray',
      value: create(PbJSONElement_JSONArraySchema, {
        nodes: toRGANodes(arr),
        createdAt: toTimeTicket(arr.getCreatedAt()),
        movedAt: toTimeTicket(arr.getMovedAt()),
        removedAt: toTimeTicket(arr.getRemovedAt()),
      }),
    },
  });
}

/**
 * `toPrimitive` converts the given model to Protobuf format.
 */
function toPrimitive(primitive: Primitive): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'primitive',
      value: create(PbJSONElement_PrimitiveSchema, {
        type: toValueType(primitive.getType()),
        value: primitive.toBytes(),
        createdAt: toTimeTicket(primitive.getCreatedAt()),
        movedAt: toTimeTicket(primitive.getMovedAt()),
        removedAt: toTimeTicket(primitive.getRemovedAt()),
      }),
    },
  });
}

/**
 * `toText` converts the given model to Protobuf format.
 */
function toText(text: CRDTText<Record<string, any>>): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'text',
      value: create(PbJSONElement_TextSchema, {
        nodes: toTextNodes(text.getRGATreeSplit()),
        createdAt: toTimeTicket(text.getCreatedAt()),
        movedAt: toTimeTicket(text.getMovedAt()),
        removedAt: toTimeTicket(text.getRemovedAt()),
      }),
    },
  });
}

/**
 * `toCounter` converts the given model to Protobuf format.
 */
function toCounter(counter: CRDTCounter): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'counter',
      value: create(PbJSONElement_CounterSchema, {
        type: toCounterType(counter.getType()),
        value: counter.toBytes(),
        createdAt: toTimeTicket(counter.getCreatedAt()),
        movedAt: toTimeTicket(counter.getMovedAt()),
        removedAt: toTimeTicket(counter.getRemovedAt()),
        hllRegisters: counter.hllBytes() || new Uint8Array(),
      }),
    },
  });
}

/**
 * `toTree` converts the given model to Protobuf format.
 */
function toTree(tree: CRDTTree): PbJSONElement {
  return create(PbJSONElementSchema, {
    body: {
      case: 'tree',
      value: create(PbJSONElement_TreeSchema, {
        nodes: toTreeNodes(tree.getRoot()),
        createdAt: toTimeTicket(tree.getCreatedAt()),
        movedAt: toTimeTicket(tree.getMovedAt()),
        removedAt: toTimeTicket(tree.getRemovedAt()),
      }),
    },
  });
}

/**
 * `toElement` converts the given model to Protobuf format.
 */
function toElement(element: CRDTElement): PbJSONElement {
  if (element instanceof CRDTObject) {
    return toObject(element);
  }
  if (element instanceof CRDTArray) {
    return toArray(element);
  }
  if (element instanceof Primitive) {
    return toPrimitive(element);
  }
  if (element instanceof CRDTText) {
    return toText(element);
  }
  if (element instanceof CRDTCounter) {
    return toCounter(element);
  }
  if (element instanceof CRDTTree) {
    return toTree(element);
  }

  throw new YorkieError(Code.ErrUnimplemented, `unimplemented element`);
}

/**
 * `toChangePack` converts the given model to Protobuf format.
 */
function toChangePack(pack: ChangePack<Indexable>): PbChangePack {
  return create(PbChangePackSchema, {
    documentKey: pack.getDocumentKey(),
    checkpoint: toCheckpoint(pack.getCheckpoint()),
    isRemoved: pack.getIsRemoved(),
    changes: toChanges(pack.getChanges()),
    snapshot: pack.getSnapshot(),
    versionVector: toVersionVector(pack.getVersionVector()),
  });
}

/**
 * `toRevisionSummary` converts a protobuf RevisionSummary to a RevisionSummary.
 */
export function toRevisionSummary(
  pbRevision: PbRevisionSummary,
): RevisionSummary {
  return {
    id: pbRevision.id,
    label: pbRevision.label,
    description: pbRevision.description,
    snapshot: pbRevision.snapshot,
    createdAt: pbRevision.createdAt
      ? timestampDate(pbRevision.createdAt)
      : new Date(),
  };
}

/**
 * `errorMetadataOf` returns the error metadata of the given connect error.
 */
export function errorMetadataOf(error: ConnectError): Record<string, string> {
  if (!(error instanceof ConnectError)) {
    return {};
  }

  // NOTE(chacha912): Currently, we only use the first detail to represent the
  // error metadata.
  const infos = error.findDetails(ErrorInfoSchema);
  for (const info of infos) {
    return info.metadata;
  }

  return {};
}

/**
 * `errorCodeOf` returns the error code of the given connect error.
 */
export function errorCodeOf(error: ConnectError): string {
  return errorMetadataOf(error).code ?? '';
}

/**
 * `isErrorCode` checks if the error is a ConnectError with the given error code.
 */
export function isErrorCode(
  error: unknown,
  code: string,
): error is ConnectError {
  return error instanceof ConnectError && errorCodeOf(error) === code;
}

/**
 * `fromChangeID` converts the given Protobuf format to model format.
 */
function fromChangeID(pbChangeID: PbChangeID): ChangeID {
  // TODO(hackerwins): Remove BigInt conversion. Some of the bigint values are
  // passed as string in the protobuf. We should fix this in the future.
  return ChangeID.of(
    pbChangeID.clientSeq,
    BigInt(pbChangeID.lamport),
    toHexString(pbChangeID.actorId),
    fromVersionVector(pbChangeID.versionVector)!,
    BigInt(pbChangeID.serverSeq),
  );
}

/**
 * `fromVersionVector` converts the given Protobuf format to model format.
 */
function fromVersionVector(
  pbVersionVector?: PbVersionVector,
): VersionVector | undefined {
  if (!pbVersionVector) {
    return;
  }

  const vector = new VersionVector();
  Object.entries(pbVersionVector.vector).forEach(([key, value]) => {
    const hexKey = bytesToHex(base64ToUint8Array(key));
    vector.set(hexKey, BigInt(value.toString()));
  });
  return vector;
}

/**
 * `fromTimeTicket` converts the given Protobuf format to model format.
 */
function fromTimeTicket(pbTimeTicket?: PbTimeTicket): TimeTicket | undefined {
  if (!pbTimeTicket) {
    return;
  }

  return TimeTicket.of(
    BigInt(pbTimeTicket.lamport),
    pbTimeTicket.delimiter,
    toHexString(pbTimeTicket.actorId),
  );
}

/**
 * `fromPresence` converts the given Protobuf format to model format.
 */
function fromPresence<P extends Indexable>(pbPresence: PbPresence): P {
  const data: Record<string, string> = {};
  Object.entries(pbPresence.data).forEach(([key, value]) => {
    data[key] = JSON.parse(value);
  });

  return data as P;
}

/**
 * `fromPresenceChange` converts the given Protobuf format to model format.
 */
function fromPresenceChange<P extends Indexable>(
  pbPresenceChange: PbPresenceChange,
): PresenceChange<P> {
  const type = pbPresenceChange.type;
  if (type === PbPresenceChange_ChangeType.PUT) {
    const presence = fromPresence<P>(pbPresenceChange.presence!);
    return {
      type: PresenceChangeType.Put,
      presence,
    };
  }
  if (type === PbPresenceChange_ChangeType.CLEAR) {
    return {
      type: PresenceChangeType.Clear,
    };
  }

  throw new YorkieError(Code.ErrInvalidType, `unsupported type: ${type}`);
}

/**
 * `fromPresences` converts the given Protobuf format to model format.
 */
function fromPresences<P extends Indexable>(pbPresences: {
  [key: string]: PbPresence;
}): Map<ActorID, P> {
  const presences = new Map<ActorID, P>();
  Object.entries(pbPresences).forEach(([actorID, pbPresence]) => {
    presences.set(actorID, fromPresence(pbPresence));
  });
  return presences;
}

/**
 * `fromValueType` converts the given Protobuf format to model format.
 */
function fromValueType(pbValueType: PbValueType): PrimitiveType {
  switch (pbValueType) {
    case PbValueType.NULL:
      return PrimitiveType.Null;
    case PbValueType.BOOLEAN:
      return PrimitiveType.Boolean;
    case PbValueType.INTEGER:
      return PrimitiveType.Integer;
    case PbValueType.LONG:
      return PrimitiveType.Long;
    case PbValueType.DOUBLE:
      return PrimitiveType.Double;
    case PbValueType.STRING:
      return PrimitiveType.String;
    case PbValueType.BYTES:
      return PrimitiveType.Bytes;
    case PbValueType.DATE:
      return PrimitiveType.Date;
  }
  throw new YorkieError(
    Code.ErrUnimplemented,
    `unimplemented value type: ${pbValueType}`,
  );
}

/**
 * `fromCounterType` converts the given Protobuf format to model format.
 */
function fromCounterType(pbValueType: PbValueType): CounterType {
  switch (pbValueType) {
    case PbValueType.INTEGER_CNT:
      return CounterType.Int;
    case PbValueType.LONG_CNT:
      return CounterType.Long;
    case PbValueType.INTEGER_DEDUP_CNT:
      return CounterType.IntDedup;
  }
  throw new YorkieError(
    Code.ErrUnimplemented,
    `unimplemented value type: ${pbValueType}`,
  );
}

/**
 * `fromElementSimple` converts the given Protobuf format to model format.
 */
function fromElementSimple(pbElementSimple: PbJSONElementSimple): CRDTElement {
  switch (pbElementSimple.type) {
    case PbValueType.JSON_OBJECT:
      if (!pbElementSimple.value) {
        return CRDTObject.create(fromTimeTicket(pbElementSimple.createdAt)!);
      }
      return bytesToObject(pbElementSimple.value);
    case PbValueType.JSON_ARRAY:
      if (!pbElementSimple.value) {
        return CRDTArray.create(fromTimeTicket(pbElementSimple.createdAt)!);
      }
      return bytesToArray(pbElementSimple.value);
    case PbValueType.TEXT:
      return CRDTText.create(
        RGATreeSplit.create(),
        fromTimeTicket(pbElementSimple.createdAt)!,
      );
    case PbValueType.TREE:
      return bytesToTree(pbElementSimple.value)!;
    case PbValueType.NULL:
    case PbValueType.BOOLEAN:
    case PbValueType.INTEGER:
    case PbValueType.LONG:
    case PbValueType.DOUBLE:
    case PbValueType.STRING:
    case PbValueType.BYTES:
    case PbValueType.DATE:
      return Primitive.of(
        Primitive.valueFromBytes(
          fromValueType(pbElementSimple.type),
          pbElementSimple.value,
        ),
        fromTimeTicket(pbElementSimple.createdAt)!,
      );
    case PbValueType.INTEGER_CNT:
    case PbValueType.LONG_CNT:
    case PbValueType.INTEGER_DEDUP_CNT:
      return CRDTCounter.create(
        fromCounterType(pbElementSimple.type),
        CRDTCounter.valueFromBytes(
          fromCounterType(pbElementSimple.type),
          pbElementSimple.value,
        ),
        fromTimeTicket(pbElementSimple.createdAt)!,
      );
  }
}

/**
 * `fromTextNodePos` converts the given Protobuf format to model format.
 */
function fromTextNodePos(pbTextNodePos: PbTextNodePos): RGATreeSplitPos {
  return RGATreeSplitPos.of(
    RGATreeSplitNodeID.of(
      fromTimeTicket(pbTextNodePos.createdAt)!,
      pbTextNodePos.offset,
    ),
    pbTextNodePos.relativeOffset,
  );
}

/**
 * `fromTextNodeID` converts the given Protobuf format to model format.
 */
function fromTextNodeID(pbTextNodeID: PbTextNodeID): RGATreeSplitNodeID {
  return RGATreeSplitNodeID.of(
    fromTimeTicket(pbTextNodeID.createdAt)!,
    pbTextNodeID.offset,
  );
}

/**
 * `fromTextNode` converts the given Protobuf format to model format.
 */
function fromTextNode(pbTextNode: PbTextNode): RGATreeSplitNode<CRDTTextValue> {
  const textValue = CRDTTextValue.create(pbTextNode.value);
  Object.entries(pbTextNode.attributes).forEach(([key, value]) => {
    textValue.setAttr(key, value.value, fromTimeTicket(value.updatedAt)!);
  });

  const textNode = RGATreeSplitNode.create(
    fromTextNodeID(pbTextNode.id!),
    textValue,
  );
  textNode.setRemovedAt(fromTimeTicket(pbTextNode.removedAt));
  return textNode;
}

/**
 * `fromTreePos` converts the given Protobuf format to model format.
 */
function fromTreePos(pbTreePos: PbTreePos): CRDTTreePos {
  return CRDTTreePos.of(
    fromTreeNodeID(pbTreePos.parentId!),
    fromTreeNodeID(pbTreePos.leftSiblingId!),
  );
}

/**
 * `fromTreeNodeID` converts the given Protobuf format to model format.
 */
function fromTreeNodeID(pbTreeNodeID: PbTreeNodeID): CRDTTreeNodeID {
  return CRDTTreeNodeID.of(
    fromTimeTicket(pbTreeNodeID.createdAt)!,
    pbTreeNodeID.offset,
  );
}

/**
 * `fromTreeNodesWhenEdit` converts the given Protobuf format to model format.
 */
function fromTreeNodesWhenEdit(
  pbTreeNodes: Array<PbTreeNodes>,
): Array<CRDTTreeNode> | undefined {
  if (!pbTreeNodes.length) {
    return;
  }

  const treeNodes: Array<CRDTTreeNode> = [];
  pbTreeNodes.forEach((node) => {
    const treeNode = fromTreeNodes(node.content);
    treeNodes.push(treeNode!);
  });

  return treeNodes;
}

/**
 * `fromTreeNodes` converts the given Protobuf format to model format.
 */
function fromTreeNodes(
  pbTreeNodes: Array<PbTreeNode>,
): CRDTTreeNode | undefined {
  if (pbTreeNodes.length === 0) {
    return;
  }

  const nodes: Array<CRDTTreeNode> = [];
  for (const pbTreeNode of pbTreeNodes) {
    nodes.push(fromTreeNode(pbTreeNode));
  }

  const root = nodes[nodes.length - 1];
  const depthTable = new Map<number, CRDTTreeNode>();
  depthTable.set(pbTreeNodes[nodes.length - 1].depth, nodes[nodes.length - 1]);
  for (let i = nodes.length - 2; i >= 0; i--) {
    const parent = depthTable.get(pbTreeNodes[i].depth - 1);
    parent!.prepend(nodes[i]);
    depthTable.set(pbTreeNodes[i].depth, nodes[i]);
  }

  root.updateDescendantsSize();
  root.updateDescendantsSize(true);

  // build CRDTTree from the root to construct the links between nodes.
  return CRDTTree.create(root, InitialTimeTicket).getRoot();
}

/**
 * `fromRHT` converts the given Protobuf format to model format.
 */
function fromRHT(pbRHT: { [key: string]: PbNodeAttr }): RHT {
  const rht = RHT.create();
  for (const [key, pbRHTNode] of Object.entries(pbRHT)) {
    rht.setInternal(
      key,
      pbRHTNode.value,
      fromTimeTicket(pbRHTNode.updatedAt)!,
      pbRHTNode.isRemoved,
    );
  }

  return rht;
}

/**
 * `fromTreeNode` converts the given Protobuf format to model format.
 */
function fromTreeNode(pbTreeNode: PbTreeNode): CRDTTreeNode {
  const id = fromTreeNodeID(pbTreeNode.id!);
  const node = CRDTTreeNode.create(id, pbTreeNode.type);
  const pbAttrs = Object.entries(pbTreeNode.attributes);
  if (node.isText) {
    node.value = pbTreeNode.value;
  } else if (pbAttrs.length) {
    node.attrs = fromRHT(pbTreeNode.attributes);
  }

  if (pbTreeNode.insPrevId) {
    node.insPrevID = fromTreeNodeID(pbTreeNode.insPrevId);
  }

  if (pbTreeNode.insNextId) {
    node.insNextID = fromTreeNodeID(pbTreeNode.insNextId);
  }

  if (pbTreeNode.mergedFrom) {
    node.mergedFrom = fromTreeNodeID(pbTreeNode.mergedFrom);
  }

  if (pbTreeNode.mergedAt) {
    node.mergedAt = fromTimeTicket(pbTreeNode.mergedAt);
  }

  node.removedAt = fromTimeTicket(pbTreeNode.removedAt);

  return node;
}

/**
 * `fromOperation` converts the given Protobuf format to model format.
 */
function fromOperation(pbOperation: PbOperation): Operation | undefined {
  if (pbOperation.body.case === 'set') {
    const pbSetOperation = pbOperation.body.value;
    return SetOperation.create(
      pbSetOperation!.key,
      fromElementSimple(pbSetOperation!.value!),
      fromTimeTicket(pbSetOperation!.parentCreatedAt)!,
      fromTimeTicket(pbSetOperation!.executedAt)!,
    );
  } else if (pbOperation.body.case === 'add') {
    const pbAddOperation = pbOperation.body.value;
    return AddOperation.create(
      fromTimeTicket(pbAddOperation!.parentCreatedAt)!,
      fromTimeTicket(pbAddOperation!.prevCreatedAt)!,
      fromElementSimple(pbAddOperation!.value!),
      fromTimeTicket(pbAddOperation!.executedAt)!,
    );
  } else if (pbOperation.body.case === 'move') {
    const pbMoveOperation = pbOperation.body.value;
    return MoveOperation.create(
      fromTimeTicket(pbMoveOperation!.parentCreatedAt)!,
      fromTimeTicket(pbMoveOperation!.prevCreatedAt)!,
      fromTimeTicket(pbMoveOperation!.createdAt)!,
      fromTimeTicket(pbMoveOperation!.executedAt)!,
    );
  } else if (pbOperation.body.case === 'remove') {
    const pbRemoveOperation = pbOperation.body.value;
    return RemoveOperation.create(
      fromTimeTicket(pbRemoveOperation!.parentCreatedAt)!,
      fromTimeTicket(pbRemoveOperation!.createdAt)!,
      fromTimeTicket(pbRemoveOperation!.executedAt)!,
    );
  } else if (pbOperation.body.case === 'edit') {
    const pbEditOperation = pbOperation.body.value;
    const attributes = new Map();
    Object.entries(pbEditOperation!.attributes).forEach(([key, value]) => {
      attributes.set(key, value);
    });
    const executedAt = fromTimeTicket(pbEditOperation!.executedAt)!;

    const fromPbSpan = (pbSpan: PbRestoreSpan): RestoreSpan<CRDTTextValue> => {
      const value = CRDTTextValue.create(pbSpan.content);
      for (const [key, attr] of Object.entries(pbSpan.attributes)) {
        value.setAttr(key, attr, executedAt);
      }
      return {
        createdAt: fromTimeTicket(pbSpan.createdAt)!,
        start: pbSpan.start,
        end: pbSpan.end,
        value,
      };
    };
    let restoreSpans: Array<RestoreSpan<CRDTTextValue>> | undefined;
    let retombstoneSpans: Array<RestoreSpan<CRDTTextValue>> | undefined;
    let restoreMode: 'restore' | 'retombstone' | undefined;
    if (
      pbEditOperation!.restoreSpans.length ||
      pbEditOperation!.retombstoneSpans.length
    ) {
      restoreSpans = pbEditOperation!.restoreSpans.map(fromPbSpan);
      retombstoneSpans = pbEditOperation!.retombstoneSpans.map(fromPbSpan);
      restoreMode =
        pbEditOperation!.restoreMode === PbRestoreMode.RETOMBSTONE
          ? 'retombstone'
          : 'restore';
    }

    return EditOperation.create(
      fromTimeTicket(pbEditOperation!.parentCreatedAt)!,
      fromTextNodePos(pbEditOperation!.from!),
      fromTextNodePos(pbEditOperation!.to!),
      pbEditOperation!.content,
      attributes,
      executedAt,
      restoreMode ? true : undefined,
      restoreSpans,
      restoreMode,
      retombstoneSpans,
    );
  } else if (pbOperation.body.case === 'style') {
    const pbStyleOperation = pbOperation.body.value;
    const attributes = new Map<string, string>();
    Object.entries(pbStyleOperation!.attributes).forEach(([key, value]) => {
      attributes.set(key, value);
    });
    const attributesToRemove = pbStyleOperation!.attributesToRemove ?? [];

    return new StyleOperation(
      fromTimeTicket(pbStyleOperation!.parentCreatedAt)!,
      fromTextNodePos(pbStyleOperation!.from!),
      fromTextNodePos(pbStyleOperation!.to!),
      attributes,
      attributesToRemove,
      fromTimeTicket(pbStyleOperation!.executedAt)!,
    );
  } else if (pbOperation.body.case === 'increase') {
    const pbIncreaseOperation = pbOperation.body.value;
    return IncreaseOperation.create(
      fromTimeTicket(pbIncreaseOperation!.parentCreatedAt)!,
      fromElementSimple(pbIncreaseOperation!.value!),
      fromTimeTicket(pbIncreaseOperation!.executedAt)!,
      pbIncreaseOperation!.actor || undefined,
    );
  } else if (pbOperation.body.case === 'treeEdit') {
    const pbTreeEditOperation = pbOperation.body.value;
    let treeRestoreSpans: Array<TreeRestoreSpan> | undefined;
    let treeRetombstoneSpans: Array<TreeRestoreSpan> | undefined;
    let treeRestoreMode: 'restore' | 'retombstone' | undefined;
    if (
      pbTreeEditOperation!.restoreSpans.length ||
      pbTreeEditOperation!.retombstoneSpans.length
    ) {
      treeRestoreSpans = pbTreeEditOperation!.restoreSpans.map(
        fromPbTreeRestoreSpan,
      );
      treeRetombstoneSpans = pbTreeEditOperation!.retombstoneSpans.map(
        fromPbTreeRestoreSpan,
      );
      treeRestoreMode =
        pbTreeEditOperation!.restoreMode === PbRestoreMode.RETOMBSTONE
          ? 'retombstone'
          : 'restore';
    }
    const treeEdit = TreeEditOperation.create(
      fromTimeTicket(pbTreeEditOperation!.parentCreatedAt)!,
      fromTreePos(pbTreeEditOperation!.from!),
      fromTreePos(pbTreeEditOperation!.to!),
      fromTreeNodesWhenEdit(pbTreeEditOperation!.contents),
      pbTreeEditOperation!.splitLevel,
      fromTimeTicket(pbTreeEditOperation!.executedAt)!,
      treeRestoreMode ? true : undefined,
      undefined,
      undefined,
      treeRestoreSpans,
      treeRestoreMode,
      treeRetombstoneSpans,
    );
    treeEdit.setSplitTickets(
      pbTreeEditOperation!.splitTickets.map(
        (ticket) => fromTimeTicket(ticket)!,
      ),
    );
    return treeEdit;
  } else if (pbOperation.body.case === 'treeStyle') {
    const pbTreeStyleOperation = pbOperation.body.value;
    const attributes = new Map();
    const attributesToRemove = pbTreeStyleOperation.attributesToRemove;

    if (attributesToRemove?.length > 0) {
      return TreeStyleOperation.createTreeRemoveStyleOperation(
        fromTimeTicket(pbTreeStyleOperation!.parentCreatedAt)!,
        fromTreePos(pbTreeStyleOperation!.from!),
        fromTreePos(pbTreeStyleOperation!.to!),
        attributesToRemove,
        fromTimeTicket(pbTreeStyleOperation!.executedAt)!,
      );
    } else {
      Object.entries(pbTreeStyleOperation!.attributes).forEach(
        ([key, value]) => {
          attributes.set(key, value);
        },
      );
      return TreeStyleOperation.create(
        fromTimeTicket(pbTreeStyleOperation!.parentCreatedAt)!,
        fromTreePos(pbTreeStyleOperation!.from!),
        fromTreePos(pbTreeStyleOperation!.to!),
        attributes,
        fromTimeTicket(pbTreeStyleOperation!.executedAt)!,
      );
    }
  } else if (pbOperation.body.case === 'arraySet') {
    const pbArraySetOperation = pbOperation.body.value;
    return ArraySetOperation.create(
      fromTimeTicket(pbArraySetOperation!.parentCreatedAt)!,
      fromTimeTicket(pbArraySetOperation!.createdAt)!,
      fromElementSimple(pbArraySetOperation!.value!),
      fromTimeTicket(pbArraySetOperation!.executedAt)!,
    );
  } else {
    throw new YorkieError(Code.ErrUnimplemented, `unimplemented operation`);
  }
}

/**
 * `fromOperations` converts the given Protobuf format to model format.
 */
function fromOperations(pbOperations: Array<PbOperation>): Array<Operation> {
  const operations = [];
  for (const pbOperation of pbOperations) {
    const operation = fromOperation(pbOperation);
    if (operation) {
      operations.push(operation);
    }
  }
  return operations;
}

/**
 * `fromChanges` converts the given Protobuf format to model format.
 */
function fromChanges<P extends Indexable>(
  pbChanges: Array<PbChange>,
): Array<Change<P>> {
  const changes: Array<Change<P>> = [];

  for (const pbChange of pbChanges) {
    changes.push(
      Change.create({
        id: fromChangeID(pbChange.id!),
        operations: fromOperations(pbChange.operations),
        presenceChange: pbChange.presenceChange
          ? fromPresenceChange(pbChange.presenceChange!)
          : undefined,
        message: pbChange.message,
      }),
    );
  }

  return changes;
}

/**
 * `fromCheckpoint` converts the given Protobuf format to model format.
 */
function fromCheckpoint(pbCheckpoint: PbCheckpoint): Checkpoint {
  return Checkpoint.of(BigInt(pbCheckpoint.serverSeq), pbCheckpoint.clientSeq);
}

/**
 * `fromChangePack` converts the given Protobuf format to model format.
 */
function fromChangePack<P extends Indexable>(
  pbPack: PbChangePack,
): ChangePack<P> {
  return ChangePack.create<P>(
    pbPack.documentKey!,
    fromCheckpoint(pbPack.checkpoint!),
    pbPack.isRemoved,
    fromChanges(pbPack.changes),
    fromVersionVector(pbPack.versionVector),
    pbPack.snapshot,
  );
}

/**
 * `fromObject` converts the given Protobuf format to model format.
 */
function fromObject(pbObject: PbJSONElement_JSONObject): CRDTObject {
  const rht = new ElementRHT();
  for (const pbRHTNode of pbObject.nodes) {
    const value = fromElement(pbRHTNode.element!);
    rht.set(pbRHTNode.key, value, value.getPositionedAt());
  }

  const obj = new CRDTObject(fromTimeTicket(pbObject.createdAt)!, rht);
  obj.setMovedAt(fromTimeTicket(pbObject.movedAt));
  obj.setRemovedAt(fromTimeTicket(pbObject.removedAt));
  return obj;
}

/**
 * `fromArray` converts the given Protobuf format to model format.
 */
function fromArray(pbArray: PbJSONElement_JSONArray): CRDTArray {
  const rgaTreeList = new RGATreeList();
  for (const pbRGANode of pbArray.nodes) {
    if (!pbRGANode.element) {
      // Dead position node (abandoned by a move).
      if (!pbRGANode.positionCreatedAt || !pbRGANode.positionRemovedAt) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'dead RGA position node missing position timestamps',
        );
      }
      const posCreatedAt = fromTimeTicket(pbRGANode.positionCreatedAt)!;
      const posRemovedAt = fromTimeTicket(pbRGANode.positionRemovedAt)!;
      rgaTreeList.addDeadPosition(posCreatedAt, posRemovedAt);
      continue;
    }

    const elem = fromElement(pbRGANode.element);
    const posMovedAt = fromTimeTicket(pbRGANode.positionMovedAt);

    if (posMovedAt) {
      if (!pbRGANode.positionCreatedAt) {
        throw new YorkieError(
          Code.ErrInvalidArgument,
          'moved RGA node missing position_created_at',
        );
      }
      const posCreatedAt = fromTimeTicket(pbRGANode.positionCreatedAt)!;
      rgaTreeList.addMovedElement(elem, posCreatedAt, posMovedAt);
    } else {
      rgaTreeList.insert(elem);
    }
  }

  const arr = new CRDTArray(fromTimeTicket(pbArray.createdAt)!, rgaTreeList);
  arr.setMovedAt(fromTimeTicket(pbArray.movedAt));
  arr.setRemovedAt(fromTimeTicket(pbArray.removedAt));
  return arr;
}

/**
 * `fromPrimitive` converts the given Protobuf format to model format.
 */
function fromPrimitive(pbPrimitive: PbJSONElement_Primitive): Primitive {
  const primitive = Primitive.of(
    Primitive.valueFromBytes(
      fromValueType(pbPrimitive.type),
      pbPrimitive.value,
    ),
    fromTimeTicket(pbPrimitive.createdAt)!,
  );
  primitive.setMovedAt(fromTimeTicket(pbPrimitive.movedAt));
  primitive.setRemovedAt(fromTimeTicket(pbPrimitive.removedAt));
  return primitive;
}

/**
 * `fromText` converts the given Protobuf format to model format.
 */
function fromText<A extends Indexable>(
  pbText: PbJSONElement_Text,
): CRDTText<A> {
  const rgaTreeSplit = new RGATreeSplit<CRDTTextValue>();

  let prev = rgaTreeSplit.getHead();
  for (const pbNode of pbText.nodes) {
    const current = rgaTreeSplit.insertAfter(prev, fromTextNode(pbNode));
    if (pbNode.insPrevId) {
      current.setInsPrev(
        rgaTreeSplit.findNode(fromTextNodeID(pbNode.insPrevId!)),
      );
    }
    prev = current;
  }
  const text = new CRDTText<A>(rgaTreeSplit, fromTimeTicket(pbText.createdAt)!);
  text.setMovedAt(fromTimeTicket(pbText.movedAt));
  text.setRemovedAt(fromTimeTicket(pbText.removedAt));
  return text;
}

/**
 * `fromCounter` converts the given Protobuf format to model format.
 */
function fromCounter(pbCounter: PbJSONElement_Counter): CRDTCounter {
  const counter = CRDTCounter.create(
    fromCounterType(pbCounter.type),
    CRDTCounter.valueFromBytes(
      fromCounterType(pbCounter.type),
      pbCounter.value,
    ),
    fromTimeTicket(pbCounter.createdAt)!,
  );
  counter.setMovedAt(fromTimeTicket(pbCounter.movedAt));
  counter.setRemovedAt(fromTimeTicket(pbCounter.removedAt));
  if (counter.isDedup() && pbCounter.hllRegisters.length > 0) {
    counter.restoreHLL(pbCounter.hllRegisters);
  }
  return counter;
}

/**
 * `fromTree` converts the given Protobuf format to model format.
 */
function fromTree(pbTree: PbJSONElement_Tree): CRDTTree {
  const root = fromTreeNodes(pbTree.nodes);
  return CRDTTree.create(root!, fromTimeTicket(pbTree.createdAt)!);
}

/**
 * `fromElement` converts the given Protobuf format to model format.
 */
function fromElement(pbElement: PbJSONElement): CRDTElement {
  if (pbElement.body.case === 'jsonObject') {
    return fromObject(pbElement.body.value!);
  } else if (pbElement.body.case === 'jsonArray') {
    return fromArray(pbElement.body.value!);
  } else if (pbElement.body.case === 'primitive') {
    return fromPrimitive(pbElement.body.value!);
  } else if (pbElement.body.case === 'text') {
    return fromText(pbElement.body.value!);
  } else if (pbElement.body.case === 'counter') {
    return fromCounter(pbElement.body.value!);
  } else if (pbElement.body.case === 'tree') {
    return fromTree(pbElement.body.value!);
  } else {
    throw new YorkieError(Code.ErrUnimplemented, `unimplemented element`);
  }
}

/**
 * `fromSchemaRules` converts the given Protobuf format to model format.
 */
function fromSchemaRules(pbRules: Array<PbRule>): Array<Rule> {
  return pbRules.map((pbRule) => {
    const rule: Record<string, unknown> = {
      path: pbRule.path,
      type: pbRule.type,
    };
    if (pbRule.treeNodes && pbRule.treeNodes.length > 0) {
      rule.treeNodes = pbRule.treeNodes.map((tn) => ({
        nodeType: tn.nodeType,
        content: tn.content,
        marks: tn.marks,
        group: tn.group,
      }));
    }
    return rule as Rule;
  });
}

/**
 * `bytesToSnapshot` creates a Snapshot from the given byte array.
 */
function bytesToSnapshot<P extends Indexable>(
  bytes?: Uint8Array,
): {
  root: CRDTObject;
  presences: Map<ActorID, P>;
} {
  if (!bytes) {
    return {
      root: CRDTObject.create(InitialTimeTicket),
      presences: new Map(),
    };
  }

  const snapshot = fromBinary(PbSnapshotSchema, bytes);
  return {
    root: fromElement(snapshot.root!) as CRDTObject,
    presences: fromPresences<P>(snapshot.presences),
  };
}

/**
 * `versionVectorToHex` converts the given VersionVector to bytes.
 */
function versionVectorToHex(vector: VersionVector): string {
  const pbVersionVector = toVersionVector(vector)!;

  return bytesToHex(toBinary(PbVersionVectorSchema, pbVersionVector));
}

/**
 * `hexToVersionVector` creates a VersionVector from the given bytes.
 */
function hexToVersionVector(hex: string): VersionVector {
  const bytes = hexToBytes(hex);
  const pbVersionVector = fromBinary(PbVersionVectorSchema, bytes);

  return fromVersionVector(pbVersionVector)!;
}

/**
 * `bytesToObject` creates an JSONObject from the given byte array.
 */
function bytesToObject(bytes?: Uint8Array): CRDTObject {
  if (!bytes) {
    throw new YorkieError(Code.ErrInvalidArgument, 'bytes is empty');
  }

  const pbElement = fromBinary(PbJSONElementSchema, bytes);
  return fromObject(pbElement.body.value! as PbJSONElement_JSONObject);
}

/**
 * `objectToBytes` converts the given JSONObject to byte array.
 */
function objectToBytes(obj: CRDTObject): Uint8Array {
  return toBinary(PbJSONElementSchema, toElement(obj));
}

/**
 * `bytesToArray` creates an CRDTArray from the given bytes.
 */
function bytesToArray(bytes?: Uint8Array): CRDTArray {
  if (!bytes) {
    throw new YorkieError(Code.ErrInvalidArgument, 'bytes is empty');
  }

  const pbElement = fromBinary(PbJSONElementSchema, bytes);
  return fromArray(pbElement.body.value! as PbJSONElement_JSONArray);
}

/**
 * `arrayToBytes` converts the given CRDTArray to bytes.
 */
function arrayToBytes(array: CRDTArray): Uint8Array {
  return toBinary(PbJSONElementSchema, toArray(array));
}

/**
 * `bytesToTree` creates an CRDTTree from the given bytes.
 */
function bytesToTree(bytes?: Uint8Array): CRDTTree {
  if (!bytes) {
    throw new YorkieError(Code.ErrInvalidArgument, 'bytes is empty');
  }

  const pbElement = fromBinary(PbJSONElementSchema, bytes);
  return fromTree(pbElement.body.value! as PbJSONElement_Tree);
}

/**
 * `treeToBytes` converts the given tree to bytes.
 */
function treeToBytes(tree: CRDTTree): Uint8Array {
  return toBinary(PbJSONElementSchema, toTree(tree));
}

/**
 * `bytesToHex` creates an hex string from the given byte array.
 */
function bytesToHex(bytes?: Uint8Array): string {
  if (!bytes) {
    return '';
  }

  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * `toHexString` converts the given byte array to hex string.
 */
function toHexString(bytes: Uint8Array): string {
  return bytesToHex(bytes);
}

/**
 * `hexToBytes` converts the given hex string to byte array.
 */
function hexToBytes(hex: string): Uint8Array {
  return new Uint8Array(
    hex.match(/.{1,2}/g)!.map((byte) => parseInt(byte, 16)),
  );
}

/**
 * `base64ToUint8Array` converts the given base64 string to Uint8Array.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  if (typeof window !== 'undefined' && typeof window.atob === 'function') {
    const binary = window.atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } else if (
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as any).Buffer !== 'undefined'
  ) {
    const Buffer = (globalThis as any).Buffer;
    return new Uint8Array(Buffer.from(base64, 'base64'));
  } else {
    throw new Error('No base64 decoder available');
  }
}

/**
 * `toUnit8Array` converts the given hex string to byte array.
 */
function toUint8Array(hex: string): Uint8Array {
  return hexToBytes(hex);
}

/**
 * `uint8ArrayToBase64` converts the given Uint8Array to base64 string.
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  } else if (
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as any).Buffer !== 'undefined'
  ) {
    const Buffer = (globalThis as any).Buffer;
    return Buffer.from(bytes).toString('base64');
  } else {
    throw new Error('No base64 encoder available');
  }
}
/**
 * `bytesToChangeID` creates a ChangeID from the given bytes.
 */
function bytesToChangeID(bytes: Uint8Array): ChangeID {
  const pbChangeID = fromBinary(PbChangeIDSchema, bytes);
  return fromChangeID(pbChangeID);
}

/**
 * `bytesToOperation` creates an Operation from the given bytes.
 */
function bytesToOperation(bytes: Uint8Array): Operation {
  const pbOperation = fromBinary(PbOperationSchema, bytes);
  return fromOperation(pbOperation)!;
}

/**
 * `converter` is a converter that converts the given model to protobuf format.
 * is also used to convert models to bytes and vice versa.
 */
export const converter = {
  fromPresence,
  toChangePack,
  fromChangePack,
  fromChanges,
  toTreeNodes,
  fromTreeNodes,
  objectToBytes,
  bytesToObject,
  bytesToSnapshot,
  bytesToHex,
  hexToBytes,
  toHexString,
  toUint8Array,
  toOperation,
  toChangeID,
  bytesToChangeID,
  bytesToOperation,
  versionVectorToHex,
  hexToVersionVector,
  fromSchemaRules,
  toRevisionSummary,
  changeIDToBinary: (changeID: ChangeID) =>
    toBinary(PbChangeIDSchema, toChangeID(changeID)),
  operationToBinary: (op: Operation) =>
    toBinary(PbOperationSchema, toOperation(op)),
};
